import {
  Button,
  Stack,
  Chip,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  LinearProgress,
  SelectChangeEvent,
  Box,
  useTheme,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ExtractParams } from '@/types/params';

import {
  ApiResponse_GetAll_PropertyManager,
  useGetAllPropertyManagers,
} from '@/api/endpoints/propertyManager/useGetAllPropertyManagers';
import { PropertyManagementWithManagerWithLimitedUserView } from '@/api/models/propertyManagement';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { PMPRoutes } from '@/config/routes';
import { formatPhoneNumber } from '@/utils/phone';

import RowActionsMenu from '@/components/common/RowActionsMenu';
import PropertyManagerCreateModal from '@/components/propertyManager/PropertyManagerCreateModal';
import PropertyManagerDeleteModal from '@/components/propertyManager/PropertyManagerDeleteModal';
import PropertyManagerUpdateModal from '@/components/propertyManager/PropertyManagerUpdateModal';

type Order = 'asc' | 'desc';
type PropertyManager = ApiResponse_GetAll_PropertyManager[0];

type SortableField = 'full_name' | 'email' | 'role';

const TABLE_HEAD = [
  { id: 'full_name' as const, label: 'Name', sortable: true, width: '30%' },
  { id: 'email' as const, label: 'Email', sortable: true, width: '30%' },
  { id: 'phone' as const, label: 'Phone', sortable: false, width: '20%' },
  { id: 'role' as const, label: 'Role', sortable: true, width: '20%' },
];

function descendingComparator(a: PropertyManager, b: PropertyManager, orderBy: SortableField) {
  if (orderBy === 'full_name') {
    const aValue = String(a.manager.limitedUser.full_name ?? '').toLowerCase();
    const bValue = String(b.manager.limitedUser.full_name ?? '').toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  if (orderBy === 'email') {
    const aValue = String(a.manager.limitedUser.email ?? '').toLowerCase();
    const bValue = String(b.manager.limitedUser.email ?? '').toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  if (orderBy === 'role') {
    const aValue = String(a.role ?? '').toLowerCase();
    const bValue = String(b.role ?? '').toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  return 0;
}

function getComparator(order: Order, orderBy: SortableField) {
  return order === 'desc'
    ? (a: PropertyManager, b: PropertyManager) => descendingComparator(a, b, orderBy)
    : (a: PropertyManager, b: PropertyManager) => -descendingComparator(a, b, orderBy);
}

const PropertyLocationViewPropertyManagerListPage: React.FC = () => {
  const { id: propertyLocationId } =
    useParams<ExtractParams<typeof PMPRoutes.propertyLocationManagerList>>();
  const theme = useTheme();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SortableField>('full_name');
  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [isPropertyManagerCreateModalOpen, setIsPropertyManagerCreateModalOpen] = useState(false);
  const [isPropertyManagerUpdateModalOpen, setIsPropertyManagerUpdateModalOpen] = useState(false);
  const [isPropertyManagerDeleteModalOpen, setIsPropertyManagerDeleteModalOpen] = useState(false);

  const [propertyManagerToUpdate, setPropertyManagerToUpdate] =
    useState<PropertyManagementWithManagerWithLimitedUserView | null>(null);
  const [propertyManagerToDelete, setPropertyManagerToDelete] =
    useState<PropertyManagementWithManagerWithLimitedUserView | null>(null);

  const getAllPropertyManagersQuery = useGetAllPropertyManagers({
    location_id: Number(propertyLocationId),
  });

  const handleRequestSort = (property: SortableField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Filter and sort the data
  const filteredData = useMemo(() => {
    let filtered = getAllPropertyManagersQuery.data || [];

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter(
        item =>
          item.manager.limitedUser.full_name?.toLowerCase().includes(filterName.toLowerCase()) ??
          false
      );
    }

    // Sort the data
    return [...filtered].sort(getComparator(order, orderBy));
  }, [getAllPropertyManagersQuery.data, filterName, order, orderBy]);

  // Get the current page of data
  const displayedData = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  const onEditPropertyManagerClick = useCallback(
    (propertyManager: PropertyManagementWithManagerWithLimitedUserView) => {
      setPropertyManagerToUpdate(propertyManager);
      setIsPropertyManagerUpdateModalOpen(true);
    },
    []
  );

  const onDeletePropertyManagerClick = useCallback(
    (propertyManager: PropertyManagementWithManagerWithLimitedUserView) => {
      setPropertyManagerToDelete(propertyManager);
      setIsPropertyManagerDeleteModalOpen(true);
    },
    []
  );

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%', position: 'relative' }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignSelf: 'flex-end',
          right: { sm: 0 },
        }}
      >
        <Button
          color="primary"
          variant="contained"
          size="medium"
          startIcon={<PlusIcon />}
          onClick={() => setIsPropertyManagerCreateModalOpen(true)}
        >
          Add Property Partner
        </Button>
      </Stack>

      <Card sx={{ bgcolor: 'common.white' }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search property partners..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD.map(headCell => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.id === 'phone' ? 'left' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      width: headCell.width,
                      borderRight: 'none',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      bgcolor: theme.palette.secondary[200],
                      textTransform: 'none',
                      color: 'text.primary',
                      '& .MuiTableSortLabel-root': {
                        textTransform: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.875rem', sm: 'inherit' },
                        fontWeight: 'bold',
                        color: 'text.primary',
                        '&:hover': {
                          color: 'text.primary',
                        },
                        '&.Mui-active': {
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        },
                      },
                    }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id as SortableField)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      <Typography
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: 'inherit' },
                          fontWeight: 'bold',
                          color: 'text.primary',
                          '&:hover': {
                            color: 'text.primary',
                          },
                        }}
                      >
                        {headCell.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    width: '50px',
                    bgcolor: theme.palette.secondary[200],
                    borderRight: 'none',
                  }}
                />
              </TableRow>
            </TableHead>

            <TableBody>
              {getAllPropertyManagersQuery.isLoading && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <LinearProgress sx={{ width: '100%' }} />
                  </TableCell>
                </TableRow>
              )}
              {!getAllPropertyManagersQuery.isLoading && displayedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No property partners found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!getAllPropertyManagersQuery.isLoading &&
                displayedData.length > 0 &&
                displayedData.map(row => (
                  <TableRow
                    key={String(row.id)}
                    sx={{
                      '&:hover': {
                        backgroundColor: `${theme.palette.secondary[200]} !important`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      },
                    }}
                  >
                    <TableCell sx={{ width: '30%' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {row.manager.limitedUser.full_name}
                        {row.manager.is_emergency_contact && (
                          <Chip
                            label="Emergency Contact"
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ width: '30%' }}>{row.manager.limitedUser.email}</TableCell>
                    <TableCell align="left" sx={{ width: '20%' }}>
                      {row.manager.limitedUser.phone
                        ? formatPhoneNumber(row.manager.limitedUser.phone)
                        : '–'}
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <Chip
                        label={row.role}
                        size="small"
                        variant="outlined"
                        color={row.role === 'ADMIN' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ width: '50px' }}>
                      <RowActionsMenu
                        items={[
                          {
                            label: 'Edit',
                            onClick: () => onEditPropertyManagerClick(row),
                          },
                          {
                            label: 'Delete',
                            onClick: () => onDeletePropertyManagerClick(row),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Card>

      {isPropertyManagerCreateModalOpen && (
        <PropertyManagerCreateModal
          onClose={() => setIsPropertyManagerCreateModalOpen(false)}
          onPropertyManagerCreated={() => {
            getAllPropertyManagersQuery.refetch();
            setIsPropertyManagerCreateModalOpen(false);
          }}
          propertyLocationId={Number(propertyLocationId)}
        />
      )}

      {isPropertyManagerUpdateModalOpen && !!propertyManagerToUpdate && (
        <PropertyManagerUpdateModal
          propertyManager={propertyManagerToUpdate}
          onClose={() => {
            setIsPropertyManagerUpdateModalOpen(false);
            setPropertyManagerToUpdate(null);
          }}
        />
      )}

      {isPropertyManagerDeleteModalOpen && !!propertyManagerToDelete && (
        <PropertyManagerDeleteModal
          propertyManager={propertyManagerToDelete}
          onClose={() => {
            setIsPropertyManagerDeleteModalOpen(false);
            setPropertyManagerToDelete(null);
          }}
        />
      )}
    </Stack>
  );
};

export default PropertyLocationViewPropertyManagerListPage;
